/**
 * EditPanel.tsx
 *
 * The collapsible "Edit & Regenerate" panel on the carousel step (D1.4).
 *
 * Desktop: slides in from the right edge as a fixed side panel (380px wide).
 *          Pressing the "Edit" vertical tab on the right edge opens it.
 * Mobile:  slides up from the bottom as a drawer (max 80vh).
 *          A floating action button (FAB) opens it.
 *
 * The panel has three sections:
 *   1. Regenerate  — slide count pills + instruction input + Regenerate button + Undo/Redo
 *   2. Edit slide  — per-field inputs for the currently active slide + Remove slide
 *   3. Brand fields (collapsed <details>) — handle, tagline, etc.
 * At the bottom: a single "Generate Carousel →" button that both approves
 * post-regen structures AND re-renders after manual field edits.
 */

import React, { useState, useEffect, useRef } from "react";
import type { BrandField, CarouselStructure, ContentSlide } from "./types";
import { primaryBtn, ACCENT, ACCENT_08, spinnerStyle } from "./sharedStyles";
import { slidesToMarkdown, markdownToSlides } from "./markdownConversionHelpers";

type EditViewMode = "slide" | "text";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  isOpen:             boolean;
  isMobile:           boolean;
  carouselStructure:  CarouselStructure;
  editableSlides:     ContentSlide[];
  currentSlide:       number;
  selectedSlideCount: number | null;
  regenInstruction:   string;
  isRestructuring:    boolean;
  pendingRender:      boolean;
  slideCountMin:      number;
  slideCountMax:      number;
  canUndo:            boolean;
  canRedo:            boolean;
  brandFields:        BrandField[];
  brandValues:        Record<string, string>;
  isSavingBrand:      boolean;
  brandSaved:         boolean;
  brandSaveError:     string | null;
  hasPanelBrandChanges: boolean;
  /** Ref: true while any panel input is focused (suppresses Ctrl+Z/Y keyboard shortcut). */
  panelFocusRef:      React.MutableRefObject<boolean>;
  setCurrentSlide:    React.Dispatch<React.SetStateAction<number>>;
  setSelectedSlideCount: (n: number) => void;
  setRegenInstruction: (v: string) => void;
  setEditableSlides:  React.Dispatch<React.SetStateAction<ContentSlide[]>>;
  setBrandValues:     React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setBrandSaved:      (v: boolean) => void;
  onClose:            () => void;
  onRegenerate:       () => void;
  onRerender:         () => void;
  onRemoveSlide:      (index: number) => void;
  onUndo:             () => void;
  onRedo:             () => void;
  onSaveBrand:        () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const EditPanel: React.FC<Props> = ({
  isOpen, isMobile, carouselStructure, editableSlides, currentSlide,
  selectedSlideCount, regenInstruction, isRestructuring, pendingRender,
  slideCountMin, slideCountMax, canUndo, canRedo,
  brandFields, brandValues, isSavingBrand, brandSaved, brandSaveError,
  hasPanelBrandChanges, panelFocusRef,
  setCurrentSlide, setSelectedSlideCount, setRegenInstruction,
  setEditableSlides, setBrandValues, setBrandSaved,
  onClose, onRegenerate, onRerender, onRemoveSlide, onUndo, onRedo, onSaveBrand,
}) => {
  // Toggle between per-slide field editor and full-document text view
  const [editViewMode, setEditViewMode] = useState<EditViewMode>("slide");

  // Text view local state — changes here don't touch editableSlides until the user saves
  const [localMarkdown,  setLocalMarkdown]  = useState(() =>
    slidesToMarkdown(editableSlides, carouselStructure.editableFields)
  );
  const [textDirty,      setTextDirty]      = useState(false);   // unsaved local edits
  const [textSaveError,  setTextSaveError]  = useState<string | null>(null);

  // Re-sync the text view whenever editableSlides changes externally (regen, undo/redo).
  // This also clears any dirty/error state because the external change is the new baseline.
  useEffect(() => {
    setLocalMarkdown(slidesToMarkdown(editableSlides, carouselStructure.editableFields));
    setTextDirty(false);
    setTextSaveError(null);
  }, [editableSlides]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const showSlidePills = slideCountMin < slideCountMax;
  const regenEnabled   =
    (selectedSlideCount !== null && selectedSlideCount !== editableSlides.length) ||
    regenInstruction.trim().length > 0;
  const atMinCount     = editableSlides.length <= slideCountMin;

  const focus = () => { panelFocusRef.current = true; };
  const blur  = () => { panelFocusRef.current = false; };

  /** Text-view keystroke: only update local state — leave editableSlides alone. */
  const handleTextEdit = (markdown: string) => {
    setLocalMarkdown(markdown);
    setTextDirty(true);
    setTextSaveError(null);
  };

  /** Save button: parse, validate, then commit to editableSlides. */
  const handleTextSave = () => {
    const parsed = markdownToSlides(localMarkdown, editableSlides, carouselStructure.editableFields);
    if (parsed.length === 0) {
      setTextSaveError("No slides found — start each slide with ## and separate them with ---");
      return;
    }
    if (parsed.length < slideCountMin) {
      setTextSaveError(`Too few slides (minimum is ${slideCountMin}) — add more ## sections`);
      return;
    }
    setEditableSlides(parsed);
    setTextDirty(false);
    setTextSaveError(null);
  };

  return (
    <div style={panelContainerStyle(isMobile)}>
      {/* Mobile drag handle */}
      {isMobile && (
        <div style={{ padding: "12px", display: "flex", justifyContent: "center" }}>
          <div style={{ width: "36px", height: "4px", borderRadius: "2px", background: "#E4E4E0" }} />
        </div>
      )}

      {/* ── Sticky header ── */}
      <div style={stickyHeaderStyle(isMobile)}>
        <span style={{ fontWeight: 800, fontSize: "15px", letterSpacing: "-0.02em", color: "#0A0A0A" }}>
          Edit &amp; Regenerate
        </span>
        <button onClick={onClose} style={closeButtonStyle}>✕</button>
      </div>

      <div style={{ padding: "20px" }}>

        {/* ── Section 1: Regenerate ── */}
        <SectionLabel>Regenerate</SectionLabel>

        {/* Slide count pills (hidden for fixed-count templates) */}
        {showSlidePills && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: "#888", fontWeight: 600, flexShrink: 0 }}>Slide count:</span>
            {Array.from({ length: slideCountMax - slideCountMin + 1 }, (_, i) => i + slideCountMin).map(n => (
              <button
                key={n}
                className={`d1-pill${selectedSlideCount === n ? " active" : ""}`}
                onClick={() => setSelectedSlideCount(n)}
              >
                {n} slides
              </button>
            ))}
          </div>
        )}

        {/* Optional instruction text */}
        <input
          type="text"
          className="d1-panel-input"
          style={{ marginBottom: "10px" }}
          placeholder='What changes? e.g. "make it punchier"'
          value={regenInstruction}
          onChange={e => setRegenInstruction(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && regenEnabled) onRegenerate(); }}
          onFocus={focus}
          onBlur={blur}
        />

        {/* Regenerate + Undo/Redo row */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "14px" }}>
          <button
            style={{
              ...primaryBtn,
              fontSize:  "13px",
              padding:   "9px 16px",
              flex:      1,
              justifyContent: "center",
              opacity:   regenEnabled ? 1 : 0.4,
              cursor:    regenEnabled ? "pointer" : "not-allowed",
            }}
            disabled={!regenEnabled}
            onClick={onRegenerate}
          >
            Regenerate ↺
          </button>
          <button className="d1-toolbar-btn" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">⟲</button>
          <button className="d1-toolbar-btn" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)">⟳</button>
        </div>

        {/* Spinner while structure API is in flight */}
        {isRestructuring && (
          <div style={{
            display: "flex", alignItems: "center", gap: "10px",
            padding: "12px 0", color: ACCENT, fontSize: "13px", fontWeight: 600,
          }}>
            <div style={{ ...spinnerStyle, width: "16px", height: "16px" }} />
            Rethinking structure…
          </div>
        )}

        {/* Hint shown after undo/redo — prompts user to click Generate Carousel */}
        {pendingRender && !isRestructuring && (
          <p style={{ fontSize: "11px", color: ACCENT, fontWeight: 600, textAlign: "center", marginBottom: 0 }}>
            ✦ Structure restored — click Generate Carousel below
          </p>
        )}

        <hr style={{ border: "none", borderTop: "1px solid #F0F0EE", margin: "18px 0" }} />

        {/* ── Section 2: Edit slide ── */}
        {/* Header row: section label + view toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <SectionLabel noMargin>Edit slide</SectionLabel>
          <div style={{ display: "flex", gap: "4px" }}>
            <EditViewBtn label="≡ Slides" active={editViewMode === "slide"} onClick={() => setEditViewMode("slide")} />
            <EditViewBtn label="</> Text" active={editViewMode === "text"}  onClick={() => setEditViewMode("text")}  />
          </div>
        </div>

        {/* ── Slide view ── */}
        {editViewMode === "slide" && (
          <>
            {/* Slide navigator */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
              <button
                className="d1-toolbar-btn"
                onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
                disabled={currentSlide === 0}
              >‹</button>
              <span style={{ fontSize: "13px", fontWeight: 600, flex: 1, textAlign: "center", color: "#0A0A0A" }}>
                Slide {currentSlide + 1} / {editableSlides.length}
              </span>
              <button
                className="d1-toolbar-btn"
                onClick={() => setCurrentSlide(s => Math.min(editableSlides.length - 1, s + 1))}
                disabled={currentSlide >= editableSlides.length - 1}
              >›</button>
            </div>

            {/* Editable fields for the active slide */}
            <SlideFields
              slide={editableSlides[currentSlide]}
              slideIndex={currentSlide}
              editableFields={carouselStructure.editableFields}
              onUpdateSlide={(key, val) => {
                setEditableSlides(prev => prev.map((s, i) =>
                  i === currentSlide ? { ...s, fields: { ...s.fields, [key]: val } } : s
                ));
              }}
              onFocus={focus}
              onBlur={blur}
            />

            {/* Remove slide */}
            <button
              onClick={() => onRemoveSlide(currentSlide)}
              disabled={atMinCount}
              style={{
                width:        "100%",
                padding:      "9px",
                marginBottom: "20px",
                border:       "1.5px solid",
                borderColor:  atMinCount ? "#E4E4E0" : "rgba(220,38,38,0.35)",
                borderRadius: "10px",
                background:   "none",
                color:        atMinCount ? "#ccc" : "rgba(220,38,38,0.75)",
                fontSize:     "13px",
                fontWeight:   600,
                cursor:       atMinCount ? "not-allowed" : "pointer",
                fontFamily:   "inherit",
                transition:   "all 0.15s",
              }}
              title={atMinCount ? "Minimum slide count reached" : `Remove slide ${currentSlide + 1}`}
            >
              Remove this slide
            </button>
          </>
        )}

        {/* ── Text view ── */}
        {editViewMode === "text" && (
          <div style={{ marginBottom: "20px" }}>
            {/* Textarea */}
            <textarea
              value={localMarkdown}
              onChange={e => handleTextEdit(e.target.value)}
              onFocus={focus}
              onBlur={blur}
              spellCheck={false}
              style={{
                width:        "100%",
                boxSizing:    "border-box",
                minHeight:    "260px",
                border:       `1.5px solid ${textSaveError ? "rgba(220,38,38,0.5)" : textDirty ? "rgba(91,79,232,0.35)" : "#E4E4E0"}`,
                borderRadius: "12px",
                padding:      "14px",
                outline:      "none",
                resize:       "vertical",
                fontFamily:   "'DM Mono', 'Fira Mono', 'Courier New', monospace",
                fontSize:     "12px",
                lineHeight:   "1.7",
                color:        "#1a1a1a",
                background:   "#FAFAF9",
                transition:   "border-color 0.15s",
              }}
            />

            {/* Hint */}
            <p style={{ fontSize: "10px", color: "#ccc", marginTop: "5px", marginBottom: "10px" }}>
              Each{" "}
              <code style={{ background: "#f0f0f0", padding: "1px 3px", borderRadius: "3px" }}>## Section</code>
              {" "}is one slide · delete an editable section to remove it · brand/cover slides are always preserved
            </p>

            {/* Error message */}
            {textSaveError && (
              <div style={{
                padding:      "9px 12px",
                borderRadius: "8px",
                background:   "rgba(220,38,38,0.06)",
                border:       "1px solid rgba(220,38,38,0.2)",
                color:        "rgba(185,28,28,0.9)",
                fontSize:     "12px",
                fontWeight:   500,
                marginBottom: "10px",
                lineHeight:   "1.5",
              }}>
                ⚠ {textSaveError}
              </div>
            )}

            {/* Save button */}
            <button
              onClick={handleTextSave}
              disabled={!textDirty}
              style={{
                width:        "100%",
                padding:      "9px",
                border:       "none",
                borderRadius: "10px",
                background:   textDirty ? ACCENT : "#F0F0EE",
                color:        textDirty ? "#fff" : "#bbb",
                fontSize:     "13px",
                fontWeight:   700,
                cursor:       textDirty ? "pointer" : "not-allowed",
                fontFamily:   "inherit",
                transition:   "all 0.15s",
              }}
            >
              {textDirty ? "Save changes" : "No unsaved changes"}
            </button>
          </div>
        )}

        {/* ── Section 3: Brand fields (collapsed) ── */}
        {brandFields.length > 0 && (
          <details style={{ marginBottom: "20px" }}>
            <summary style={{
              fontSize: "11px", fontWeight: 700, color: "#aaa",
              letterSpacing: "0.08em", textTransform: "uppercase",
              cursor: "pointer", padding: "8px 0",
              borderTop: "1px solid #F0F0EE",
              userSelect: "none",
            }}>
              Brand fields ▸
            </summary>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "12px" }}>
              {brandFields.map(field => (
                <div key={field.key}>
                  <label style={{
                    display: "block", fontSize: "11px", fontWeight: 700, color: "#888",
                    letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px",
                  }}>
                    {field.label}
                  </label>
                  <input
                    type="text"
                    className="d1-panel-input"
                    placeholder={field.placeholder}
                    value={brandValues[field.key] ?? ""}
                    onChange={e => {
                      setBrandValues(prev => ({ ...prev, [field.key]: e.target.value }));
                      setBrandSaved(false);
                    }}
                    onFocus={focus}
                    onBlur={blur}
                  />
                </div>
              ))}
              {hasPanelBrandChanges && !brandSaved && (
                <button
                  style={{
                    ...primaryBtn, fontSize: "12px", padding: "8px 14px",
                    opacity: isSavingBrand ? 0.6 : 1,
                    cursor:  isSavingBrand ? "not-allowed" : "pointer",
                  }}
                  disabled={isSavingBrand}
                  onClick={onSaveBrand}
                >
                  {isSavingBrand ? "Saving…" : "Save to profile"}
                </button>
              )}
              {brandSaved    && <span style={{ fontSize: "12px", color: "#27ae60", fontWeight: 600 }}>✓ Saved to profile</span>}
              {brandSaveError && <span style={{ fontSize: "12px", color: "#c0392b" }}>⚠ {brandSaveError}</span>}
            </div>
          </details>
        )}

        {/* ── Generate Carousel — unified CTA ── */}
        {/*
         * pendingRender=true  → glowing purple gradient (post-regen / undo approval)
         * pendingRender=false → dark button (manual field edit re-render)
         */}
        <button
          style={{
            ...primaryBtn,
            width:          "100%",
            justifyContent: "center",
            fontSize:       "14px",
            background:     pendingRender
              ? `linear-gradient(135deg, ${ACCENT} 0%, #7C3AED 100%)`
              : "#0A0A0A",
          }}
          onClick={onRerender}
        >
          Generate Carousel →
        </button>
        <p style={{ fontSize: "11px", color: "#aaa", textAlign: "center", marginTop: "8px" }}>
          Generates carousel from your current edits
        </p>
      </div>
    </div>
  );
};

// ── SlideFields (local sub-component) ─────────────────────────────────────────

/**
 * Renders the editable inputs for one slide, based on the editableFields
 * config from the carousel structure. Supports text, textarea, and list fields.
 */
const SlideFields: React.FC<{
  slide?:         ContentSlide;
  slideIndex:     number;
  editableFields: Record<string, { key: string; inputType: string }[]>;
  onUpdateSlide:  (key: string, val: string) => void;
  onFocus:        () => void;
  onBlur:         () => void;
}> = ({ slide, editableFields, onUpdateSlide, onFocus, onBlur }) => {
  if (!slide) return null;

  const fields = editableFields[slide.slotType] ?? [];

  if (fields.length === 0) {
    return (
      <p style={{ fontSize: "13px", color: "#aaa", textAlign: "center", padding: "16px 0" }}>
        No editable fields on this slide.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginBottom: "16px" }}>
      {fields.map(field => {
        const val = String(slide.fields[field.key] ?? "");
        // Convert camelCase key to a readable label ("bodyText" → "body Text")
        const label = field.key.replace(/([A-Z])/g, " $1").trim();

        return (
          <div key={field.key}>
            <label style={{
              display: "block", fontSize: "11px", fontWeight: 700, color: "#888",
              letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px",
            }}>
              {label}
            </label>
            {field.inputType === "textarea" ? (
              <textarea
                className="d1-panel-textarea"
                value={val}
                onChange={e => onUpdateSlide(field.key, e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            ) : (
              <input
                type="text"
                className="d1-panel-input"
                value={val}
                onChange={e => onUpdateSlide(field.key, e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── SectionLabel ─────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; noMargin?: boolean }> = ({ children, noMargin }) => (
  <p style={{
    fontSize:      "10px",
    fontWeight:    700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color:         "#aaa",
    marginBottom:  noMargin ? 0 : "12px",
  }}>
    {children}
  </p>
);

// ── EditViewBtn ───────────────────────────────────────────────────────────────

/** Small toggle pill for switching between Slide and Text edit views. */
const EditViewBtn: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label, active, onClick,
}) => (
  <button
    onClick={onClick}
    style={{
      padding:      "3px 10px",
      borderRadius: "6px",
      fontSize:     "11px",
      fontWeight:   600,
      cursor:       "pointer",
      fontFamily:   "inherit",
      transition:   "all 0.15s",
      border:       active ? `1.5px solid ${ACCENT}` : "1.5px solid #E4E4E0",
      background:   active ? ACCENT_08 : "#fff",
      color:        active ? ACCENT : "#aaa",
    }}
  >
    {label}
  </button>
);

// ── Styles ────────────────────────────────────────────────────────────────────

const panelContainerStyle = (isMobile: boolean): React.CSSProperties =>
  isMobile ? {
    position:     "fixed",
    left:         0,
    right:        0,
    bottom:       0,
    maxHeight:    "80vh",
    borderRadius: "20px 20px 0 0",
    animation:    "slideUpPanel 360ms cubic-bezier(.22,1,.36,1) both",
    boxShadow:    "0 -8px 32px rgba(0,0,0,0.14)",
    overflowY:    "auto",
    background:   "#fff",
    zIndex:       200,
    padding:      "0 0 40px",
    fontFamily:   "'Bricolage Grotesque', sans-serif",
  } : {
    position:     "fixed",
    right:        0,
    top:          56, // below sticky nav
    bottom:       0,
    width:        "380px",
    borderLeft:   "1px solid #E4E4E0",
    animation:    "slideInRight 320ms cubic-bezier(.22,1,.36,1) both",
    boxShadow:    "-4px 0 24px rgba(0,0,0,0.07)",
    overflowY:    "auto",
    background:   "#fff",
    zIndex:       200,
    padding:      "0 0 40px",
    fontFamily:   "'Bricolage Grotesque', sans-serif",
  };

const stickyHeaderStyle = (isMobile: boolean): React.CSSProperties => ({
  display:        "flex",
  alignItems:     "center",
  justifyContent: "space-between",
  padding:        isMobile ? "0 20px 14px" : "20px 20px 14px",
  borderBottom:   "1px solid #E4E4E0",
  position:       "sticky",
  top:            0,
  background:     "#fff",
  zIndex:         2,
});

const closeButtonStyle: React.CSSProperties = {
  width:          "28px",
  height:         "28px",
  borderRadius:   "50%",
  background:     "#F5F5F3",
  border:         "none",
  cursor:         "pointer",
  display:        "flex",
  alignItems:     "center",
  justifyContent: "center",
  fontSize:       "14px",
  color:          "#666",
  fontWeight:     700,
  fontFamily:     "inherit",
};

export default EditPanel;
